import { useMemo } from 'react';
import dayjs from 'dayjs';
import { CallIntelQueries, CallIntelTypes, CallIntelUtils } from '@frontend/api-call-intel';
import { API_DATE_FORMAT } from '../constants';
import { ComparisonPeriodType } from '../types';

const STALE_TIME = 5 * 60 * 1000; // 5 minutes

interface CallIntelStatsComparisonDataProps {
  comparisonPeriod: 'week' | 'month';
  locationIds: string[];
  isDemoAccount?: boolean;
  statsIncludes?: CallIntelTypes.StatsIncludes;
  includesAnalysis?: CallIntelTypes.IncludesAnalysis[];
  filters?: CallIntelTypes.ApiPayloadProps['filters'];
  fetchOverviewDetails?: boolean;
}

type DateRangeMapKey = keyof typeof CallIntelUtils.defaultDateRangeMap;

const ComparisonPeriodDateRangeMap: Record<
  ComparisonPeriodType,
  { current: DateRangeMapKey; previous: DateRangeMapKey }
> = {
  week: {
    current: 'this-week',
    previous: 'last-week',
  },
  month: {
    current: 'this-month',
    previous: 'last-month',
  },
};

export const useCallIntelStatsComparisonData = ({
  comparisonPeriod = 'week',
  locationIds,
  isDemoAccount = false,
  statsIncludes = { opportunities: false, serviceQuality: false },
  filters = {},
  includesAnalysis = [],
  fetchOverviewDetails = true,
}: CallIntelStatsComparisonDataProps) => {
  const { currentOverviewRequest, previousOverviewRequest } = useMemo(() => {
    const commonFilters: CallIntelTypes.ApiPayloadProps['filters'] = {
      ...filters,
      locations: locationIds,
    };

    const currentDateRange = CallIntelUtils.defaultDateRangeMap[ComparisonPeriodDateRangeMap[comparisonPeriod].current];
    const previousDateRange =
      CallIntelUtils.defaultDateRangeMap[ComparisonPeriodDateRangeMap[comparisonPeriod].previous];

    const currentOverviewRequest: CallIntelTypes.ApiPayloadProps = {
      filters: {
        ...commonFilters,
        startDate: dayjs(currentDateRange.startDate).format(API_DATE_FORMAT),
        endDate: dayjs(currentDateRange.endDate).format(API_DATE_FORMAT),
      },
      includesAnalysis,
    };

    const previousOverviewRequest: CallIntelTypes.ApiPayloadProps = {
      filters: {
        ...commonFilters,
        startDate: dayjs(previousDateRange.startDate).format(API_DATE_FORMAT),
        endDate: dayjs(previousDateRange.endDate).format(API_DATE_FORMAT),
      },
      includesAnalysis,
    };

    return {
      currentOverviewRequest,
      previousOverviewRequest,
    };
  }, [locationIds, filters, includesAnalysis, comparisonPeriod]);

  const { data: currentOverviewDetails, isLoading: isLoadingOverviewDetails } = CallIntelQueries.useGetOverviewDetails<
    CallIntelTypes.GetCallsOverviewIO['output']
  >({
    isDemoAccount,
    demoData: null as any, // Adding any as this field will be ignored
    payload: currentOverviewRequest,
    realQueryOptions: {
      staleTime: STALE_TIME,
      enabled: fetchOverviewDetails,
    },
  });

  const { data: previousOverviewDetails, isLoading: isLoadingPreviousOverviewDetails } =
    CallIntelQueries.useGetOverviewDetails<CallIntelTypes.GetCallsOverviewIO['output']>({
      isDemoAccount,
      demoData: null as any, // Adding any as this field will be ignored
      payload: previousOverviewRequest,
      realQueryOptions: {
        staleTime: STALE_TIME,
        enabled: fetchOverviewDetails,
      },
    });

  const { data: currentStats, isLoading: isLoadingCurrentStats } = CallIntelQueries.useGetOverviewStats({
    demoData: {} as CallIntelTypes.OverviewStatsResponse,
    isDemoAccount: false,
    payload: {
      filters: currentOverviewRequest.filters,
      includes: statsIncludes,
    },
    realQueryOptions: {
      staleTime: STALE_TIME,
    },
  });

  const { data: previousStats, isLoading: isLoadingPreviousStats } = CallIntelQueries.useGetOverviewStats({
    demoData: {} as CallIntelTypes.OverviewStatsResponse,
    isDemoAccount,
    payload: {
      filters: previousOverviewRequest.filters,
      includes: statsIncludes,
    },
    realQueryOptions: {
      staleTime: STALE_TIME,
    },
  });

  return {
    currentOverviewDetails,
    previousOverviewDetails,
    currentStats,
    previousStats,
    isLoading:
      isLoadingOverviewDetails || isLoadingPreviousOverviewDetails || isLoadingCurrentStats || isLoadingPreviousStats,
  };
};
